<template>
  <div class="main-reg-container">
    <div
      v-if="isLoading"
      class="loading-overlay"
    >
      <div class="spinner" />
    </div>

    <!-- Avatar -->
    <label
      for="avatarInput"
      class="avatar-wrapper"
    >
      <img
        :src="avatarUrl"
        alt="Avatar"
        class="avatar-image"
      />

      <input
        id="avatarInput"
        ref="avatarInputRef"
        type="file"
        accept="image/*"
        hidden
        @change="handleFileUpload"
      />
    </label>

    <!-- User data -->
    <div class="input-fields">
      <RegistrationTextField
        v-model="fullName"
        placeholder="Имя Фамилия"
        @blur="validateFullName"
      />

      <RegistrationTextField
        v-model="age"
        placeholder="Возраст"
        @blur="validateAge"
      />

      <RegistrationTextField
        v-model="specifics"
        placeholder="Специфика"
      />
    </div>

    <!-- Employer -->
    <div class="employer-card">
      <div class="employer-card-header">
        <div class="employer-icon">
          💼
        </div>

        <div class="employer-copy">
          <p class="working-status-text">
            Планируете размещать вакансии?
          </p>

          <p class="working-status-hint">
            Это поможет настроить профиль под ваши задачи
          </p>
        </div>
      </div>

      <div class="switcher">
        <button
          type="button"
          class="switch-button"
          :class="{ active: !isEmployer }"
          @click="setEmployer(false)"
        >
          Нет
        </button>

        <button
          type="button"
          class="switch-button"
          :class="{ active: isEmployer }"
          @click="setEmployer(true)"
        >
          Да
        </button>
      </div>
    </div>
  </div>

  <!-- Messages -->
  <ErrorToast
    v-if="errorMessage"
    :message="errorMessage"
  />

  <WarningToast
    v-if="warningMessage"
    :message="warningMessage"
  />

  <Snackbar
    message="Данные успешно обновлены!"
    type="success"
    :show="successSnackbar"
  />

  <!-- Cropper -->
  <div
    v-if="showCropper"
    class="cropper-modal"
  >
    <div class="cropper-container">
      <Cropper
        ref="cropperRef"
        :src="cropperImage"
        :stencil-component="CircleStencil"
        :stencil-props="{ aspectRatio: 1 }"
        :stencil-size="{ width: 500, height: 500 }"
        :resize-image="true"
        :move-image="true"
      />

      <div class="cropper-buttons">
        <button
          type="button"
          @click="closeCropper"
        >
          Отмена
        </button>

        <button
          type="button"
          class="primary-button"
          @click="confirmCrop"
        >
          Готово
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";

import {
  CircleStencil,
  Cropper,
} from "vue-advanced-cropper";

import "vue-advanced-cropper/dist/style.css";

import RegistrationTextField from "../RegistrationTextField.vue";
import ErrorToast from "../../components/ErrorToast.vue";
import WarningToast from "../../components/WarningToast.vue";
import Snackbar from "../../components/Snackbar.vue";
import UserService from "../../api/UserService";

// MARK: - Emits

const emit = defineEmits([
  "complete-step",
  "form-valid",
]);

// MARK: - Constants

const EMPTY_AVATAR = "/empty_avatar.png";

const STORAGE_KEYS = {
  fullName: "full_name",
  age: "age",
  specifics: "specifics",
  avatarUrl: "avatar_url",
  avatarPreview: "avatar_preview",
  isEmployer: "isEmployer",
};

const MIN_AGE = 18;
const MAX_AGE = 99;
const THUMBNAIL_SIZE = 20;

// MARK: - State

const fullName = ref("");
const age = ref("");
const specifics = ref("");
const isEmployer = ref(false);

const isLoading = ref(false);

const errorMessage = ref("");
const warningMessage = ref("");
const successSnackbar = ref(false);

// MARK: - Avatar

const avatarUrl = ref(
  localStorage.getItem(STORAGE_KEYS.avatarUrl) ||
    EMPTY_AVATAR
);

const imagePreviewBase64 = ref(
  localStorage.getItem(STORAGE_KEYS.avatarPreview) ||
    ""
);

const avatarInputRef = ref(null);

// MARK: - Cropper

const showCropper = ref(false);
const cropperImage = ref(null);
const cropperRef = ref(null);

let tempImageUrl = null;

// MARK: - Computed

const normalizedAge = computed(() => {
  const value = Number(age.value);

  return Number.isFinite(value)
    ? value
    : null;
});

const isAgeValid = computed(() => {
  const value = normalizedAge.value;

  return (
    value !== null &&
    value >= MIN_AGE &&
    value <= MAX_AGE
  );
});

const isFullNameValid = computed(() => {
  const words = fullName.value
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return words.length >= 2;
});

const hasAvatar = computed(() => {
  return (
    Boolean(avatarUrl.value) &&
    avatarUrl.value !== EMPTY_AVATAR
  );
});

const isFormValid = computed(() => {
  return (
    isFullNameValid.value &&
    isAgeValid.value &&
    specifics.value.trim().length > 0 &&
    hasAvatar.value
  );
});

// MARK: - Validation

const validateFullName = () => {
  if (isFullNameValid.value) {
    clearError();
    return true;
  }

  showError(
    "Введите имя и фамилию (минимум 2 слова)"
  );

  return false;
};

const validateAge = () => {
  if (!age.value) {
    return false;
  }

  if (isAgeValid.value) {
    clearError();
    return true;
  }

  showError(
    `Возраст должен быть от ${MIN_AGE} до ${MAX_AGE} лет`
  );

  return false;
};

const validateForm = () => {
  errorMessage.value = "";
  warningMessage.value = "";

  const isNameValid = validateFullName();
  const isUserAgeValid = validateAge();

  if (
    !isNameValid ||
    !isUserAgeValid ||
    !isFormValid.value
  ) {
    warningMessage.value =
      "Пожалуйста, заполните все поля корректно";

    return false;
  }

  return true;
};

// MARK: - Messages

const showError = (message) => {
  errorMessage.value = message;
};

const clearError = () => {
  errorMessage.value = "";
};

const triggerSnackbar = () => {
  successSnackbar.value = false;

  setTimeout(() => {
    successSnackbar.value = true;
  }, 10);
};

// MARK: - Employer

const setEmployer = (value) => {
  isEmployer.value = value;
};

// MARK: - Avatar

const handleFileUpload = (event) => {
  const file = event.target.files?.[0];

  if (!file) {
    return;
  }

  clearTempImageUrl();

  tempImageUrl = URL.createObjectURL(file);
  cropperImage.value = tempImageUrl;
  showCropper.value = true;
};

const confirmCrop = async () => {
  const result = cropperRef.value?.getResult();

  if (!result?.canvas) {
    showError(
      "Не удалось обработать изображение"
    );
    return;
  }

  isLoading.value = true;

  let previewObjectUrl = null;

  try {
    const blob = await canvasToBlob(
      result.canvas,
      "image/jpeg",
      0.9
    );

    if (!blob) {
      throw new Error(
        "Не удалось создать изображение"
      );
    }

    previewObjectUrl = URL.createObjectURL(blob);

    // Сразу показываем результат пользователю.
    avatarUrl.value = previewObjectUrl;

    const thumbnail =
      await generateThumbnail(blob);

    imagePreviewBase64.value = thumbnail;

    localStorage.setItem(
      STORAGE_KEYS.avatarPreview,
      thumbnail
    );

    const { url } =
      await UserService.uploadUserAvatar(
        blob,
        false
      );

    avatarUrl.value = url;

    localStorage.setItem(
      STORAGE_KEYS.avatarUrl,
      url
    );

    triggerSnackbar();
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки аватара:",
      error
    );

    showError(
      "Не удалось загрузить изображение"
    );
  } finally {
    if (previewObjectUrl) {
      URL.revokeObjectURL(
        previewObjectUrl
      );
    }

    isLoading.value = false;
    closeCropper();
  }
};

const closeCropper = () => {
  showCropper.value = false;
  cropperImage.value = null;

  clearTempImageUrl();

  if (avatarInputRef.value) {
    avatarInputRef.value.value = "";
  }
};

const clearTempImageUrl = () => {
  if (!tempImageUrl) {
    return;
  }

  URL.revokeObjectURL(tempImageUrl);
  tempImageUrl = null;
};

// MARK: - Image Helpers

const canvasToBlob = (
  canvas,
  type = "image/jpeg",
  quality = 0.9
) => {
  return new Promise((resolve) => {
    canvas.toBlob(
      resolve,
      type,
      quality
    );
  });
};

const generateThumbnail = (blob) => {
  return new Promise(
    (resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const image = new Image();

        image.onload = () => {
          const canvas =
            document.createElement("canvas");

          const context =
            canvas.getContext("2d");

          if (!context) {
            reject(
              new Error(
                "Canvas context unavailable"
              )
            );
            return;
          }

          canvas.width = THUMBNAIL_SIZE;
          canvas.height = THUMBNAIL_SIZE;

          context.drawImage(
            image,
            0,
            0,
            THUMBNAIL_SIZE,
            THUMBNAIL_SIZE
          );

          const base64Image = canvas
            .toDataURL("image/png")
            .split(",")[1];

          resolve(base64Image);
        };

        image.onerror = reject;

        image.src =
          event.target?.result;
      };

      reader.onerror = reject;

      reader.readAsDataURL(blob);
    }
  );
};

// MARK: - Submit

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  isLoading.value = true;

  try {
    const phoneNumber =
      localStorage.getItem(
        "phone_number"
      ) || "";

    const userData = {
      name: fullName.value.trim(),
      age: age.value.trim(),
      phone_number: phoneNumber,
      specifics: specifics.value.trim(),
      image_url: avatarUrl.value,
      image_preview:
        imagePreviewBase64.value,
      is_employer: isEmployer.value,
    };

    console.log(
      "📦 createWorkerProfile:",
      userData
    );

    await UserService.createWorkerProfile(
      userData
    );

    triggerSnackbar();

    setTimeout(() => {
      emit("complete-step", {
        isEmployer: isEmployer.value,
      });
    }, 500);

  } catch (error) {
    console.error(
      "❌ Ошибка при создании профиля:",
      error
    );

    showError(
      error?.message ||
        "Не удалось создать профиль. Попробуйте позже"
    );
  } finally {
    isLoading.value = false;
    saveForm();
  }
};

// MARK: - Storage

const saveForm = () => {
  localStorage.setItem(
    STORAGE_KEYS.fullName,
    fullName.value.trim()
  );

  localStorage.setItem(
    STORAGE_KEYS.age,
    age.value.trim()
  );

  localStorage.setItem(
    STORAGE_KEYS.specifics,
    specifics.value.trim()
  );

  localStorage.setItem(
    STORAGE_KEYS.isEmployer,
    String(isEmployer.value)
  );
};

const restoreForm = () => {
  fullName.value =
    localStorage.getItem(
      STORAGE_KEYS.fullName
    ) || "";

  age.value =
    localStorage.getItem(
      STORAGE_KEYS.age
    ) || "";

  specifics.value =
    localStorage.getItem(
      STORAGE_KEYS.specifics
    ) || "";

  const storedEmployer =
    localStorage.getItem(
      STORAGE_KEYS.isEmployer
    );

  if (storedEmployer !== null) {
    isEmployer.value =
      storedEmployer === "true";
  }
};

// MARK: - Watches

watch(
  isFormValid,
  (value) => {
    emit("form-valid", value);
  },
  {
    immediate: true,
  }
);

watch(fullName, (value) => {
  localStorage.setItem(
    STORAGE_KEYS.fullName,
    value
  );
});

watch(age, (value) => {
  const sanitized = String(value)
    .replace(/\D/g, "")
    .slice(0, 2);

  if (sanitized !== value) {
    age.value = sanitized;
    return;
  }

  localStorage.setItem(
    STORAGE_KEYS.age,
    sanitized
  );
});

watch(specifics, (value) => {
  localStorage.setItem(
    STORAGE_KEYS.specifics,
    value
  );
});

watch(isEmployer, (value) => {
  localStorage.setItem(
    STORAGE_KEYS.isEmployer,
    String(value)
  );
});

// MARK: - Lifecycle

onMounted(() => {
  restoreForm();

  document.addEventListener(
    "submit-main-reg",
    handleSubmit
  );
});

onUnmounted(() => {
  document.removeEventListener(
    "submit-main-reg",
    handleSubmit
  );

  clearTempImageUrl();
});
</script>

<style scoped>
.main-reg-container {
  position: relative;
  background: var(--header-bg-dark);
}

[data-theme="light"] .main-reg-container {
  background: var(--header-bg-light);
}

/* Avatar */

.avatar-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 140px;
  height: 140px;

  overflow: hidden;

  border: 2px solid #2887f7;
  border-radius: 50%;

  cursor: pointer;

  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.avatar-wrapper:hover {
  transform: scale(1.04);
  opacity: 0.85;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Fields */

.input-fields {
  display: flex;
  flex-direction: column;

  gap: 15px;
  margin-top: 24px;
}

/* Employer */

.working-status-text {
  margin-top: 20px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;

  color: var(--main-text-dark);
}

[data-theme="light"] .working-status-text {
  color: var(--main-text-light);
}

.switcher {
  display: flex;

  width: 126px;

  gap: 10px;
  padding: 4px;
  margin-top: 10px;

  background:
    var(--switch-registration-bg-dark);

  border-radius: 30px;
}

[data-theme="light"] .switcher {
  background:
    var(--switch-registration-bg-light);
}

.switch-button {
  flex: 1;

  padding: 8px 16px;

  border: none;
  border-radius: 30px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;

  color: white;
  background: transparent;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    transform 0.2s ease;
}

.switch-button.active {
  background: #2277e6;
}

.switch-button:active {
  transform: scale(0.96);
}

/* Loading */

.loading-overlay {
  position: fixed;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100vw;
  height: 100vh;

  background: rgba(0, 0, 0, 0.65);

  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);

  z-index: 99999;
}

.spinner {
  width: 50px;
  height: 50px;

  border: 5px solid
    rgba(255, 255, 255, 0.3);

  border-top-color: #fff;
  border-radius: 50%;

  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Cropper */

.cropper-modal {
  position: fixed;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100vw;
  height: 100vh;

  background: rgba(0, 0, 0, 0.85);

  z-index: 10000;
}

.cropper-container {
  width: 420px;
  max-width: 90vw;

  padding: 20px;

  background: #1f1f1f;

  border-radius: 16px;
}

.cropper-container .vue-advanced-cropper {
  height: 400px;
}

.cropper-buttons {
  display: flex;
  justify-content: space-between;

  gap: 12px;
  margin-top: 12px;
}

.cropper-buttons button {
  padding: 10px 18px;

  border: none;
  border-radius: 10px;

  font-family: "Eastman-Medium", sans-serif;

  cursor: pointer;
}

.cropper-buttons .primary-button {
  color: #fff;
  background: #2277e6;
}

/* Mobile */

@media (max-width: 768px) {
  .main-reg-container {
    display: flex;
    flex-direction: column;
    align-items: center;

    padding: 0 20px;
  }

  .avatar-wrapper {
    width: 110px;
    height: 110px;
  }

  .input-fields {
    width: 100%;
    max-width: 320px;

    gap: 12px;
    margin-top: 20px;
  }

  .working-status-text {
    margin-top: 18px;
    font-size: 13px;
  }

  .switcher {
    width: 120px;
  }

  .switch-button {
    padding: 7px 14px;
  }

  .cropper-container .vue-advanced-cropper {
    height: 320px;
  }
}

/* Employer */

.employer-card {
  margin-top: 26px;
  padding: 18px;

  background:
    linear-gradient(
      135deg,
      rgba(34, 119, 230, 0.12),
      rgba(40, 135, 247, 0.04)
    );

  border: 1px solid rgba(40, 135, 247, 0.25);
  border-radius: 18px;
}

[data-theme="light"] .employer-card {
  background:
    linear-gradient(
      135deg,
      rgba(34, 119, 230, 0.08),
      rgba(40, 135, 247, 0.02)
    );

  border-color: rgba(34, 119, 230, 0.18);
}

.employer-card-header {
  display: flex;
  align-items: center;

  gap: 12px;
}

.employer-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 42px;
  height: 42px;

  flex-shrink: 0;

  font-size: 20px;

  background: rgba(34, 119, 230, 0.15);

  border-radius: 12px;
}

.employer-copy {
  min-width: 0;
}

.working-status-text {
  margin: 0;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 15px;
  line-height: 1.35;

  color: var(--main-text-dark);
}

[data-theme="light"] .working-status-text {
  color: var(--main-text-light);
}

.working-status-hint {
  margin: 4px 0 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.4;

  color: var(--main-text-dark);

  opacity: 0.55;
}

[data-theme="light"] .working-status-hint {
  color: var(--main-text-light);
}

.switcher {
  display: grid;
  grid-template-columns: 1fr 1fr;

  gap: 6px;

  width: 100%;

  padding: 4px;
  margin-top: 16px;

  background: rgba(0, 0, 0, 0.14);

  border-radius: 12px;
}

[data-theme="light"] .switcher {
  background: rgba(0, 0, 0, 0.05);
}

.switch-button {
  min-height: 38px;

  padding: 8px 16px;

  border: none;
  border-radius: 9px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;

  color: var(--main-text-dark);
  background: transparent;

  opacity: 0.6;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    opacity 0.2s ease,
    transform 0.15s ease,
    box-shadow 0.2s ease;
}

[data-theme="light"] .switch-button {
  color: var(--main-text-light);
}

.switch-button.active {
  color: #fff;

  background: #2277e6;

  opacity: 1;

  box-shadow:
    0 4px 12px rgba(34, 119, 230, 0.25);
}

.switch-button:active {
  transform: scale(0.97);
}

</style>